import { FC, useCallback } from 'react';
import { Menu, MenuItem, ListItemIcon, Typography } from '@mui/material';
import { CgPlayStopO as StopIcon } from 'react-icons/cg';
import { CgPlayButtonO as PlayIcon } from 'react-icons/cg';
import { MdDateRange as DateRangeIcon } from 'react-icons/md';
import { MdDeleteOutline as DeleteOutlineIcon } from 'react-icons/md';
import { ApiKey, ApiKeyStatus } from '../types/api-key-types';
import {
  openDeleteApiKeyModal,
  openSetApiKeyExpirationModal,
  openSetApiKeyStatusModal,
  setSelectedApiKey,
} from '../store-actions/update-api-key-store-actions';
import { Nullable } from 'common-types';

const ApiKeyMenu: FC<React.PropsWithChildren<ApiKeyMenuProps>> = ({
  apiKey,
  open,
  anchorEl,
  handleClose,
  isUserAdminRole,
}) => {
  const onMenuClick = useCallback(() => {
    setSelectedApiKey(apiKey);
  }, [apiKey]);

  const enableDisableIcon =
    apiKey.apiKeyStatus === ApiKeyStatus.Active ? <StopIcon /> : <PlayIcon />;

  const onClickEnableDisable = useCallback(() => {
    handleClose();
    openSetApiKeyStatusModal();
  }, [handleClose]);

  const onClickDelete = useCallback(() => {
    handleClose();
    openDeleteApiKeyModal();
  }, [handleClose]);

  const onClickExpiration = useCallback(() => {
    handleClose();
    openSetApiKeyExpirationModal();
  }, [handleClose]);

  return (
    <Menu
      key={apiKey.id}
      id='api-key-settings-menu'
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      onClick={onMenuClick}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}>
      <MenuItem disabled={!isUserAdminRole} onClick={onClickEnableDisable}>
        <ListItemIcon>{enableDisableIcon}</ListItemIcon>
        <Typography color='text.primary' variant='body2'>
          Disable / Enable API Key
        </Typography>
      </MenuItem>
      <MenuItem disabled={!isUserAdminRole} onClick={onClickDelete}>
        <ListItemIcon>
          <DeleteOutlineIcon />
        </ListItemIcon>
        <Typography color='text.primary' variant='body2'>
          Delete API Key
        </Typography>
      </MenuItem>
      <MenuItem disabled={!isUserAdminRole} onClick={onClickExpiration}>
        <ListItemIcon>
          <DateRangeIcon />
        </ListItemIcon>
        <Typography color='text.primary' variant='body2'>
          Set Expiration
        </Typography>
      </MenuItem>
    </Menu>
  );
};

interface ApiKeyMenuProps {
  apiKey: ApiKey;
  open: boolean;
  anchorEl: null | Nullable<HTMLElement>;
  handleClose: () => void;
  isUserAdminRole: boolean;
}

export default ApiKeyMenu;
